import { useEffect, useRef, useState } from 'react';
import type { WireIncident, WireService } from '@shared/contract';
import {
  STATUS_GLYPH,
  dependencies,
  duration,
  latency,
  quota,
  terminalWorkers,
  uptime,
} from '@/lib/format';
import { SeverityChip, StatePill } from './chips';

interface Props {
  service: WireService;
  incidents: WireIncident[];
  onClose: () => void;
  onRunCheck: (checkId: number) => Promise<void>;
}

const MAX_RECENT_INCIDENTS = 8;

export function ServiceDrawer({ service, incidents, onClose, onRunCheck }: Props) {
  const deps = dependencies(service);
  const workers = terminalWorkers(service);
  const recent = incidents.filter((i) => i.serviceId === service.id).slice(0, MAX_RECENT_INCIDENTS);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes, and focus moves into the dialog so keyboard users are not
  // left tabbing through the page behind the scrim.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="drawer">
      <div className="drawer__scrim" onClick={onClose} />
      <aside className="drawer__panel" role="dialog" aria-modal="true" aria-labelledby="drawer-title">
        <button ref={closeRef} className="drawer__close" onClick={onClose} aria-label="Close details">
          &times;
        </button>

        <h2 id="drawer-title">{service.name}</h2>
        <div className="drawer__sub">
          {service.key} · {service.group} · {service.env} · tier {service.tier}
        </div>

        <h3>Overview</h3>
        <dl className="kv">
          <dt>Status</dt>
          <dd className={`st-${service.status}`}>
            <strong>{service.status}</strong>
            {service.inMaintenance && <span className="muted"> (in maintenance)</span>}
          </dd>
          <dt>24h uptime</dt>
          <dd>{uptime(service.uptime24h)}</dd>
          <dt>Host</dt>
          <dd>{service.host ? <code>{service.host}</code> : '—'}</dd>
          <dt>Depends on</dt>
          <dd>
            {service.deps.length
              ? service.deps.map((dep, i) => (
                  <span key={dep}>
                    {i > 0 && ', '}
                    <code>{dep}</code>
                  </span>
                ))
              : '—'}
          </dd>
          <dt>Runbook</dt>
          <dd>{service.runbook ? <code>{service.runbook}</code> : '—'}</dd>
        </dl>

        <h3>Checks</h3>
        <div className="panel">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {service.checks.map((check) => (
                  <tr key={check.checkId}>
                    <td>
                      <code>{check.key}</code>
                      {check.lastError && <div className="drawer__err">{check.lastError}</div>}
                    </td>
                    <td className={`st-${check.status} nowrap`}>{check.status}</td>
                    <td className="nowrap">{latency(check.lastLatencyMs)}</td>
                    <td className="muted nowrap">{duration(check.since)}</td>
                    <td>
                      <RunCheckButton checkId={check.checkId} onRun={onRunCheck} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {workers.length > 0 && (
          <>
            <h3>Terminal workers</h3>
            <div className="panel">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Worker</th>
                      <th>State</th>
                      <th>Securities today</th>
                      <th>This month</th>
                      <th>Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workers.map((worker) => (
                      <tr key={worker.id}>
                        <td>
                          <span className={`worker__dot st-${worker.status}`} aria-hidden="true">
                            {STATUS_GLYPH[worker.status]}
                          </span>{' '}
                          <code>{worker.id}</code>
                        </td>
                        <td className={`st-${worker.status} nowrap`}>
                          {worker.note ?? 'connected'}
                        </td>
                        <td className="nowrap">{quota(worker.dailyUsed, worker.dailyCap)}</td>
                        <td className="nowrap">{quota(worker.monthlyUsed, worker.monthlyCap)}</td>
                        <td className="nowrap">
                          {worker.dailyRequests == null
                            ? '—'
                            : worker.dailyRequests.toLocaleString('en-US')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {deps.length > 0 && (
          <>
            <h3>Dependency health</h3>
            <div className="panel">
              {deps.map((dep) => (
                <div className="deprow" key={dep.name}>
                  <span className="deprow__name">{dep.name}</span>
                  <span>
                    {dep.error && <span className="muted">{dep.error} </span>}
                    {dep.latencyMs != null && <span className="muted">{dep.latencyMs}ms </span>}
                    <strong className={`st-${dep.status}`}>{dep.status}</strong>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {recent.length > 0 && (
          <>
            <h3>Recent incidents</h3>
            <div className="panel">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Sev</th>
                      <th>Detail</th>
                      <th>Opened</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((incident) => (
                      <tr key={incident.id}>
                        <td>
                          <SeverityChip severity={incident.severity} />
                        </td>
                        <td>{incident.summary}</td>
                        <td className="muted nowrap">{duration(incident.openedAt)} ago</td>
                        <td>
                          <StatePill
                            state={
                              incident.resolvedAt
                                ? 'resolved'
                                : incident.ackedAt
                                  ? 'acked'
                                  : 'open'
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function RunCheckButton({
  checkId,
  onRun,
}: {
  checkId: number;
  onRun: (id: number) => Promise<void>;
}) {
  const [running, setRunning] = useState(false);
  return (
    <button
      className="btn"
      disabled={running}
      onClick={async () => {
        setRunning(true);
        try {
          await onRun(checkId);
        } finally {
          setRunning(false);
        }
      }}
    >
      {running ? 'Running…' : 'Run now'}
    </button>
  );
}
